import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { TEXT } from "~/constants/text";
import { LiveViewingScreen } from "~/features/live/components/LiveViewingScreen";
import { useLiveViewing } from "~/features/live/hooks/useLiveViewing";
import type { LiveChannel } from "~/features/live/types";
import { useSwipeBackControl } from "~/hooks/useSwipeBackControl";
import { useLiveStore } from "~/stores/useLiveStore";

const LiveViewingPage = () => {
  useSwipeBackControl();

  const phase = useLiveStore((s) => s.phase);
  const currentChannel = useLiveStore((s) => s.currentChannel);

  const { enterLive, leaveLive, sendChatMessage } = useLiveViewing();

  const [channel] = useState<LiveChannel | null>(() => {
    const raw = sessionStorage.getItem("live_channel");
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LiveChannel;
    } catch {
      return null;
    }
  });
  const hasEnteredRef = useRef(false);

  useEffect(() => {
    if (!channel) return;
    if (phase !== "idle") return;
    if (hasEnteredRef.current) return;
    hasEnteredRef.current = true;
    enterLive(channel);
  }, [channel, phase, enterLive]);

  if (phase === "connecting" || phase === "idle") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <p className="text-sm text-zinc-400">{TEXT.common.loading}</p>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-zinc-950">
        <p className="text-sm text-red-400">{TEXT.common.error}</p>
      </div>
    );
  }

  const displayChannel = currentChannel ?? channel;
  if (!displayChannel) {
    return null;
  }

  return (
    <LiveViewingScreen
      channel={displayChannel}
      onLeave={leaveLive}
      onSendMessage={sendChatMessage}
    />
  );
};

export const Route = createFileRoute("/_authenticated/call/live/")({
  beforeLoad: () => {
    const raw = sessionStorage.getItem("live_channel");
    if (!raw) {
      throw redirect({ to: "/discover" });
    }
  },
  component: LiveViewingPage,
});
